import { CollectionPopupComponent } from '../../../../shared';
import { PendingTrackRef, SavedTrackRef } from '../../shared/contracts/playback-controller';
import { VOYAGE_TRACK_SPANS } from '../toolbar/track.controller';
import {
  describeVoyageTrack,
  fallbackVesselColor,
  isPathlessTrack,
  resolveVesselIdentity,
} from '../panels/track-history/voyage-track-history.model';
import {
  syncWalletFromError,
  voyageSpendErrorMessage,
} from '../voyage-credits.model';
import { AisMapFeature } from '../../shared/contracts/ais-map.feature';
import {
  ActiveVesselRef,
  AisMapContext,
} from '../../shared/contracts/ais-map.context';
import { TrackLayers } from './track-layers';
import { vrHost } from '../vr-host';
import { TrackPoint } from '../../shared/contracts/vessel.types';

/**
 * What this feature must be TOLD rather than know (phase-6 boundary):
 */
export interface TrackFeatureOptions {
  /**
   * Fold a paid response's balance + filed track into the sensor doc. Stays
   * on the facade because the HOST calls it too (a panel-side refresh spends
   * through the same path), and it re-broadcasts every price line.
   */
  applyCreditSpend: (res: any, sensorId: string | undefined, cost: number) => void;
  /** Re-push every price line after a wallet-moving response. */
  syncCreditLines: () => void;
}

/**
 * TRACK MODE — one vessel's recorded path: the paid fetch, the free replay of
 * an already-purchased track, and the toolbar that drives both. A pluggable
 * feature (phase 6, REFACTOR-PLAN.md §7; PROVIDER-OWNED per decision D7).
 *
 * The biggest feature in the map, and deliberately the last extracted: it was
 * the proof that the contract holds for something far larger than the three
 * drawer copies phase 5 unified. It contributes NO popup button — a track is
 * entered from the toolbar, not from the vessel card — which is itself worth
 * noting: `popupActions()` is optional, and a feature that never overrides it
 * contributes nothing and renders nothing.
 *
 * Drawing lives in `TrackLayers`, which this owns (see that file's header).
 */
export class TrackFeature extends AisMapFeature {
  /** Everything Leaflet about the drawn path — owned, not a peer. */
  readonly layers: TrackLayers;

  constructor(ctx: AisMapContext, private readonly opts: TrackFeatureOptions) {
    super(ctx);
    this.layers = new TrackLayers(ctx);
  }

  /** What one paid track fetch costs on this provider, in its own units. */
  private price(): number {
    return this.ctx.spend?.priceFor('track') ?? 0;
  }

  /**
   * The track bar's callbacks. `onRenderTrack` and `onTrackLoaded` are the
   * transport driving the drawing; the rest are the bar's buttons.
   */
  override attach(): void {
    const track = vrHost(this.ctx.host).track;
    track.onRenderTrack = (frame) => this.layers.renderTrackFrame(frame);
    track.onTrackLoaded = (full, steps) => this.layers.buildTrackLayers(full, steps);
    track.onActiveChange = (vessel) => {
      this.ctx.selection.applyActiveVisual(vessel);
      // The dim button counts the hulls it would fade, and the traced one is not
      // among them — so the count moves when the selection does, not only when
      // the layer does.
      vrHost(this.ctx.host).track.setOthersOnMap(
        this.ctx.snapshot.othersOnMap(vessel?.mmsi ?? null),
      );
    };
    track.onClearActive = () => this.ctx.selection.deactivateVessel();
    track.onRequestFull = (minutes) => this.confirmFullTrack(minutes);
    track.onRequestSaved = (trackId) => this.replaySavedTrack(trackId);
    track.onExit = () => this.layers.clearTrackLayers();
    track.onLabelsToggle = (show) => this.layers.toggleGhostLabel(show);
    track.onDimOthersToggle = (dim) =>
      this.layers.setTrackFocus(dim ? this.layers.tracedMmsi : null);
  }

  override syncCredits(remaining: number | null, spendingClosed: boolean): void {
    vrHost(this.ctx.host).track.setCreditsRemaining(remaining, this.price(), spendingClosed);
  }

  /** Already-purchased tracks for an MMSI — replayable for free. */
  savedTracksFor(mmsi: string): SavedTrackRef[] {
    const history: any[] = Array.isArray(this.ctx.sensor?.trackHistory)
      ? this.ctx.sensor.trackHistory
      : [];
    return history
      .filter((t) => t?.trackId && String(t.mmsi) === String(mmsi))
      .sort((a, b) => new Date(b.name).getTime() - new Date(a.name).getTime());
  }

  // ── The paid fetch and the free replay ──────────────────────────────────────
  /**
   * Pre-flight gate for the paid full-track fetch.
   *
   * There is no confirm-before-spend dialog: picking a period and then pressing
   * Trace is already two deliberate clicks, and the drawer states the price
   * ("Uses 5 credits · N remaining") directly above the button. A modal
   * repeating that was a third click saying nothing new.
   */
  confirmFullTrack(minutes: number): void {
    const vessel = this.ctx.selection.active;
    if (!vessel) return;
    // One paid fetch at a time — two in flight together would both pass the
    // server's pre-flight credit check and spend two operator calls.
    if (vrHost(this.ctx.host).track.snapshot().trackLoading) return;
    const refusal = (this.ctx.spend ? this.ctx.spend.refuse('track', this.price()) : null);
    if (refusal) {
      this.ctx.host.popupService.openPopup(CollectionPopupComponent, refusal, '', '40%');
      return;
    }
    this.traceVesselFull(minutes);
  }

  /**
   * PAID track: the operator's recorded path for the active vessel.
   *
   * Private, because it is the only spend in this controller with its gate in a
   * SEPARATE method — the other four check and fetch in one body, so there is
   * no ungated entrance to reach. `confirmFullTrack` is that entrance.
   */
  private traceVesselFull(minutes: number): void {
    const vessel = this.ctx.selection.active;
    if (!vessel) return;
    const sensor = this.ctx.sensor;
    vrHost(this.ctx.host).track.setTrackLoading(true, {
      trackId: null, // a paid fetch has no stored id until it lands
      source: 'full',
      mmsi: vessel.mmsi,
      label: vessel.name,
      // The selection's name is already resolved (`_adoptTrackVessel` and the
      // popup both put the shared resolver's label there), so an unnamed hull
      // arrives as "MMSI …" — which is a stand-in, not a broadcast name.
      isNamed: !!vessel.name && vessel.name !== `MMSI ${vessel.mmsi}` && vessel.name !== vessel.mmsi,
      color: vessel.color,
      // Unknowable before the operator answers — the pending row says "buying"
      // rather than inventing a count. The span is what was asked for, so it
      // can be shown.
      points: null,
      spanLabel: VOYAGE_TRACK_SPANS.find((s) => s.minutes === minutes)?.label ?? null,
      window: null,
    });
    this.ctx.host.apiService
      .getVoyageRadarHistoricalData({
        taskid: this.ctx.host.collection?.taskId,
        sensorId: sensor?.id,
        mmsi: vessel.mmsi,
        vesselName: vessel.name,
        shipType: vessel.shipType,
        endTime: vessel.timestampIso,
        inputTime: minutes,
        startTimeType: 'minutes',
        // Human label for the saved-track chips ("24 h" reads better than
        // "1440 minutes" once the track is in trackHistory).
        spanLabel: VOYAGE_TRACK_SPANS.find((s) => s.minutes === minutes)?.label,
      })
      .subscribe(
        (res: any) => {
          vrHost(this.ctx.host).track.setTrackLoading(false);
          const data = res?.data ?? [];
          if (!data.length) {
            // The operator does not bill empty results, so nothing was spent.
            this.ctx.host.popupService.openPopup(
              CollectionPopupComponent,
              'There are no recorded positions for this vessel in the selected period. Try a longer span.',
              '',
              '40%'
            );
            return;
          }
          this.opts.applyCreditSpend(res, sensor?.id, this.price());
          vrHost(this.ctx.host).track.setSpan(minutes);
          const points = this.toTrackPoints(data);
          if (points.length < 2) {
            // The credit is already spent (the operator returned data), so say
            // so plainly rather than leaving the map silently unchanged. The
            // server does not file a track this short — `res.track` is null and
            // `applyCreditSpend` above has nothing to push — so this popup is
            // the ONLY account of the fetch the user gets. It is also why the
            // Vessel Tracks list stays free of rows that cannot be replayed.
            this.ctx.host.popupService.openPopup(
              CollectionPopupComponent,
              TrackFeature.SINGLE_POSITION_TRACK +
                ' The fetch has been used — try a longer span.',
              '',
              '40%'
            );
            return;
          }
          // `applyCreditSpend` has already pushed `res.track` onto the sensor's
          // trackHistory, so handing its id over is what marks the row the panel
          // is about to render as the one on the map.
          vrHost(this.ctx.host).track.loadTrack(points, 'full', res?.track?.trackId ?? null);
        },
        (err: any) => {
          vrHost(this.ctx.host).track.setTrackLoading(false);
          // OPERATOR_CREDITS_EXHAUSTED (platform-side) is checked before the
          // wallet branch inside the mapper — it must never read as "your
          // credits are spent". A wallet 402 carries the server's balance, so
          // re-sync before the popup: the local number is wrong by definition.
          syncWalletFromError(err, this.ctx.sensor);
          this.opts.syncCreditLines();
          const message = voyageSpendErrorMessage(err, 'track', this.price());
          this.ctx.host.popupService.openPopup(CollectionPopupComponent, message, '', '40%');
        }
      );
  }

  /**
   * The fact a one-position track is answered with, wherever it surfaces.
   *
   * Deliberately NOT phrased as a failure. The vessel reported once inside the
   * window, the operator billed for that, and the stored file is intact and
   * downloadable — the only thing absent is a second position to draw a line
   * to. Each site appends its own clause, because what to do about it differs:
   * a fresh fetch has just spent a credit, a replay has not.
   */
  private static readonly SINGLE_POSITION_TRACK =
    'Only one position was recorded for this vessel in the selected period, ' +
    'so there is no path to draw.';

  /**
   * FREE replay of a track this task already paid for: the points are recalled
   * from S3 by id, so no operator call and no credit is involved.
   *
   * Reachable from two places that differ in what the user has selected: the map
   * toolbar's trace drawer, where the vessel was clicked a moment ago, and the
   * Vessel Tracks panel, where it may not be on the map at all. The selection is
   * therefore SYNTHESISED from the record when it is missing — see
   * `_adoptTrackVessel`. Without it an off-map replay would draw a track with no
   * halo, no toolbar title and the default blue instead of the vessel's colour.
   */
  async replaySavedTrack(trackId: string): Promise<void> {
    const sensor = this.ctx.sensor;
    if (!sensor || !trackId) return;
    const record = this.findTrackRecord(trackId);
    // Answered from the record, before the two round trips: a track of one
    // position cannot be drawn, and `count` already says so. Only rows written
    // before the server stopped filing these can be in that state; the panel
    // and the trace chips make them unpressable, so this is the backstop for
    // every other way in.
    if (isPathlessTrack(record)) {
      this.ctx.host.popupService.openPopup(
        CollectionPopupComponent,
        TrackFeature.SINGLE_POSITION_TRACK +
          " The position itself is still in this track's download.",
        '',
        '40%'
      );
      return;
    }
    vrHost(this.ctx.host).track.setTrackLoading(true, this.pendingRefFor(record, trackId));
    // The map answers on the click, not on the response: the hull gets its ring
    // and the view slides onto it while the two fetches run. Without this the
    // map is inert for the whole wait and the path then teleports into place.
    const anchored = this.anchorPendingTrack(record);
    try {
      const res: any = await this.ctx.host.apiService
        .getVoyageRadarTrack({
          taskid: this.ctx.host.collection?.taskId,
          sensorId: sensor.id,
          trackId,
        })
        .toPromise();
      const url = res?.result?.imageUrl;
      if (!url) throw new Error('missing track url');
      const json: any = await this.ctx.host.httpClient.get(url).toPromise();
      const records = Array.isArray(json) ? json : json?.records ?? json?.features ?? [];
      const points = this.toTrackPoints(records);
      vrHost(this.ctx.host).track.setTrackLoading(false);
      // A file that loaded perfectly and holds ONE position is not a failure —
      // it is what the operator recorded, and saying "could not be loaded"
      // blames the archive for a fact about the window. Only an unusable file
      // (nothing readable in it) falls through to the catch. Reachable for a
      // legacy row whose `count` did not admit how little was stored — a
      // truncated response reports the operator's total, not the file's.
      if (points.length === 1) {
        if (anchored) this.ctx.selection.applyActiveVisual(this.ctx.selection.active);
        this.ctx.host.popupService.openPopup(
          CollectionPopupComponent,
          TrackFeature.SINGLE_POSITION_TRACK +
            " The position itself is still in this track's download.",
          '',
          '40%'
        );
        return;
      }
      if (points.length < 2) throw new Error('empty track');
      // Before loadTrack, never after: `setActiveVessel` tears the current track
      // down, so adopting afterwards would clear the one just installed.
      this._adoptTrackVessel(res?.result?.track ?? record, points);
      vrHost(this.ctx.host).track.loadTrack(points, 'saved', trackId);
    } catch {
      vrHost(this.ctx.host).track.setTrackLoading(false);
      // Put the ring back where it was. The anchor moved it onto a hull on the
      // strength of a track that never arrived, and a selection ring pointing at
      // a vessel nobody selected outlives the dialog below by the rest of the
      // session.
      if (anchored) this.ctx.selection.applyActiveVisual(this.ctx.selection.active);
      this.ctx.host.popupService.openPopup(
        CollectionPopupComponent,
        'That saved track could not be loaded. Please try again.',
        '',
        '40%'
      );
    }
  }

  /**
   * What the pending ON MAP row says while a saved track is being recalled.
   *
   * Built from the record the user clicked, so the wait names the same hull,
   * span and point count as the row that started it. The colour is resolved the
   * way the panel resolves it — live feed first, deterministic fallback second —
   * so the pending rail is already the colour the path will be drawn in.
   */
  private pendingRefFor(record: any, trackId: string): PendingTrackRef {
    const mmsi = String(record?.mmsi ?? '');
    const live = mmsi ? this.ctx.snapshot.liveVessels.get(mmsi) ?? null : null;
    const identity = resolveVesselIdentity(record, live?.name);
    const described = describeVoyageTrack(record, Date.now());
    const rawColor = String(live?.color ?? '');
    return {
      trackId,
      source: 'saved',
      mmsi: mmsi || null,
      label: identity.label,
      isNamed: identity.isNamed,
      color: /^#[0-9a-fA-F]{3,8}$/.test(rawColor)
        ? rawColor
        : mmsi
        ? fallbackVesselColor(mmsi)
        : null,
      points: described.points,
      spanLabel: described.spanLabel || null,
      window: described.window || null,
    };
  }

  /**
   * Gives the map something to say while the track is still in the air.
   *
   * Only for a hull that is actually on the map: it takes the selection ring
   * immediately and the view slides onto it, so the click has a visible
   * consequence within a frame rather than after two round trips. A hull that
   * has drifted out of the snapshot is left alone — there is no honest place to
   * point at, and the stored record carries no bounding box to guess from.
   *
   * A pan, never a zoom. `buildTrackLayers` will fit the real extent when the
   * points land, and pre-zooming to a guess would only make that a second jump.
   *
   * Returns whether the ring was moved, so a load that fails can put it back.
   */
  private anchorPendingTrack(record: any): boolean {
    const mmsi = String(record?.mmsi ?? '');
    if (!mmsi || !this.ctx.host.map) return false;
    const marker = this.ctx.selection.liveMarkerFor(mmsi);
    const latlng = marker?.getLatLng?.();
    if (!latlng) return false;

    const live = this.ctx.snapshot.liveVessels.get(mmsi) ?? null;
    const rawColor = String(live?.color ?? '');
    this.ctx.selection.applyActiveVisual({
      mmsi,
      name: resolveVesselIdentity(record, live?.name).label,
      color: /^#[0-9a-fA-F]{3,8}$/.test(rawColor) ? rawColor : fallbackVesselColor(mmsi),
      lat: latlng.lat,
      lng: latlng.lng,
      timestampIso: '',
      shipType: 'OTHER',
    });
    // Left where it is when the hull is already comfortably in view: moving a
    // map the user has just arranged, to a point already on it, reads as the
    // map losing their place rather than as progress.
    if (!this.ctx.host.map.getBounds().pad(-0.15).contains(latlng)) {
      this.ctx.host.map.panTo(latlng, { animate: !this.layers.prefersReducedMotion(), duration: 0.4 } as any);
    }
    return true;
  }

  /** The stored record for a trackId, or null. */
  findTrackRecord(trackId: string): any | null {
    const history: any[] = Array.isArray(this.ctx.sensor?.trackHistory)
      ? this.ctx.sensor.trackHistory
      : [];
    return history.find((t) => t?.trackId === trackId) ?? null;
  }

  /**
   * Makes the track's vessel the selected one, building a `VoyageActiveVessel`
   * out of the stored record when it is not on the map.
   *
   * A track can outlive the snapshot it was bought from — the vessel may have
   * left the AOI, or the map may be empty — and before the Vessel Tracks panel
   * existed that track was simply unreachable. Everything downstream (the halo,
   * the trail colour, the ghost's icon, the toolbar title) reads `this.ctx.selection.active`,
   * so a synthetic selection is what makes an off-map replay look like any other.
   *
   * No-op when the right vessel is already selected: `setActiveVessel` clears
   * the loaded track, so calling it needlessly would undo a replay in progress.
   */
  private _adoptTrackVessel(record: any, points: TrackPoint[]): void {
    const mmsi = String(record?.mmsi ?? '');
    if (!mmsi || this.ctx.selection.active?.mmsi === mmsi) return;

    const live = this.ctx.snapshot.liveVessels.get(mmsi) ?? null;
    const identity = resolveVesselIdentity(record, live?.name);
    const rawColor = String(live?.color ?? '');
    const start = points[0];

    this.ctx.selection.deactivateVessel();
    // Through `setActive`, not a field write: the selection lives on the
    // selection controller now, and a synthesised one is exactly the case its
    // port documents — a hull with no marker to have been clicked.
    const adopted: ActiveVesselRef = {
      mmsi,
      // The panel and the map must agree on what this vessel is called, so the
      // label comes from the shared resolver rather than a second `?? mmsi`.
      name: identity.label,
      color: /^#[0-9a-fA-F]{3,8}$/.test(rawColor) ? rawColor : fallbackVesselColor(mmsi),
      lat: start.lat,
      lng: start.lng,
      timestampIso: start.timeIso,
      shipType: identity.shipType ?? 'OTHER',
    };
    this.ctx.selection.setActive(adopted);
    this.opts.syncCreditLines();
    vrHost(this.ctx.host).track.setSavedTracks(this.savedTracksFor(mmsi));
    vrHost(this.ctx.host).track.setActiveVessel(adopted);
  }

  /**
   * Normalizes track records into transport points. Accepts a raw
   * `/v1/vessel/path` TrackPoint, a saved-track record, or a GeoJSON Feature —
   * the same shapes `toVoyageVesselFeature` handles. Read directly rather than
   * through that mapper so a 90-day track doesn't allocate a Feature per point.
   */
  toTrackPoints(records: any[]): TrackPoint[] {
    const points: TrackPoint[] = [];
    for (const r of records || []) {
      const p = r?.properties || r || {};
      const coords = r?.geometry?.coordinates;
      const lat = Number(coords ? coords[1] : p.latitude ?? p.Lat);
      const lng = Number(coords ? coords[0] : p.longitude ?? p.Lng);
      const timeIso = p.timestamp ?? p.time_utc ?? p['Timestamp UTC'];
      if (!isFinite(lat) || !isFinite(lng) || !timeIso) continue;
      points.push({
        lat,
        lng,
        course: Number(p.cog ?? p.Course ?? p.heading ?? 0) || 0,
        timeIso,
        sog: Number(p.sog ?? p.Speed),
      });
    }
    return points;
  }

  /**
   * Drops what this feature owns on a task switch — FIELDS ONLY (invariant
   * I1). The toolbar teardown (`track.close()`, dropping the selection)
   * stays on the facade's reset: it is the ORDER of those calls relative to
   * the shared map sweep that is load-bearing, and the facade owns that order.
   */
  override reset(): void {
    this.layers.dropRefs();
  }
}
